import * as amplitude from "@amplitude/analytics-browser";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
import type { User, EmailAccount } from "@/types/user";

const AMPLITUDE_API_KEY = import.meta.env.VITE_AMPLITUDE_API_KEY as string;
const SESSION_REPLAY_SAMPLE_RATE = Number(import.meta.env.VITE_AMPLITUDE_SESSION_REPLAY_SAMPLE_RATE) || 0;

let initialized = false;

export function initAnalytics() {
  if (initialized || !AMPLITUDE_API_KEY) {
    return;
  }

  amplitude.init(AMPLITUDE_API_KEY, {
    autocapture: {
      elementInteractions: false,
      fileDownloads: true,
      formInteractions: true,
      pageViews: true,
      sessions: true,
    },
    defaultTracking: {
      sessions: true,
      pageViews: true,
      formInteractions: true,
      fileDownloads: true,
    },
  });

  const sessionReplay = sessionReplayPlugin({
    sampleRate: SESSION_REPLAY_SAMPLE_RATE,
  });
  amplitude.add(sessionReplay);

  initialized = true;

  const source = new URLSearchParams(window.location.search).get("source");
  if (source) {
    const identifyObj = new amplitude.Identify();
    identifyObj.setOnce("source", source);
    amplitude.identify(identifyObj);
  }
}

const definePermission = (emails: EmailAccount[]) => {
  const status = {
    granted: 0,
    revoked: 0,
    missing: 0,
  };

  emails.forEach(email => {
    console.log(email)
    if (email?.has_permission_to_sync && email?.enabled_email_sync) {
      status.granted += 1;
    } else if (email?.has_permission_to_sync && !email?.enabled_email_sync) {
      status.revoked += 1;
    } else {
      status.missing += 1;
    }
  });

  if (status.granted > 0) {
    return 'Granted';
  } else if (status.revoked > 0) {
    return 'Revoked';
  } else {
    return 'Missing';
  }
};

export function identifyUser(user: User) {
  if (!initialized) return;

  amplitude.setUserId(String(user.id));

  const emailAccounts = user.email_accounts ?? [];

  const syncEmails = emailAccounts
    .filter((a) => !a.deleted)
    .map((a) => a.domain)
    .join(", ");

  const latestSync = emailAccounts
    .map((a) => a.last_synced)
    .filter(Boolean)
    .sort()
    .pop() ?? "Never";

  const identifyObj = new amplitude.Identify();
  identifyObj.set("uuid", user.id);
  identifyObj.set("email", user.email);
  identifyObj.set("first_name", user.first_name);
  identifyObj.set("last_name", user.last_name);
  identifyObj.set("email_type", user.registration_method);
  identifyObj.set("gmail_sync_accounts", syncEmails);
  identifyObj.set("sync_status", latestSync);
  identifyObj.set("permission_status", definePermission(emailAccounts)); // TODO: On sign in / login this is coming as blank
  identifyObj.set("num_products", user.products_count);
  identifyObj.set("user_status", user.is_staff ? "Internal" : "External");
  identifyObj.set("onboarding_completed_at", user.completed_onboarding_at || "Not completed");
  identifyObj.set("created_at", user.created_at);
  identifyObj.set("gmail_account_associated", user.gmail_account_associated || "Not Provided");
  identifyObj.set("gmail_capable_domain_present", user.gmail_capable_domain_present || "Not Provided");
  identifyObj.set("gmail_read_permission_enabled", user.gmail_read_permission_enabled || "Not Provided");
  amplitude.identify(identifyObj);
}

export function resetUser() {
  if (!initialized) return;
  amplitude.reset();
}

export function trackEvent(eventName: string, properties?: Record<string, unknown>) {
  if (!initialized) return;
  amplitude.track(eventName, properties);
}

// ── Onboarding Events ──

export function trackOnboardingStarted() {
  trackEvent("Onboarding Started");
}

export function trackDepartmentSelected(department: string, selectedDepartments: string[]) {
  trackEvent("Department Selected", { department, selected_departments: selectedDepartments });
}

export function trackDepartmentDeselected(department: string, selectedDepartments: string[]) {
  trackEvent("Department Deselected", { department, selected_departments: selectedDepartments });
}

export function trackDepartmentsContinue(selectedDepartments: string[], selectedCollections: string[]) {
  trackEvent("Departments Continue", {
    selected_departments: selectedDepartments,
    department_count: selectedDepartments.length,
    selected_collections: selectedCollections,
    collection_count: selectedCollections.length,
  });
}

export function trackOtherDepartmentSubmitted(customDepartment: string, selectedDepartments: string[]) {
  trackEvent("Other Department Submitted", { custom_department: customDepartment, selected_departments: selectedDepartments });
}

export function trackCollectionSelected(collection: string, selectedCollections: string[]) {
  trackEvent("Collection Selected", { collection, selected_collections: selectedCollections });
}

export function trackCollectionDeselected(collection: string, selectedCollections: string[]) {
  trackEvent("Collection Deselected", { collection, selected_collections: selectedCollections });
}

export function trackConnectEmailViewed(source: "signup" | "login") {
  trackEvent("Connect Email Viewed", { source });
}

export function trackEmailProviderClicked(provider: string) {
  trackEvent("Email Provider Clicked", { provider });
}

export function trackSyncCompleted(progress: number) {
  trackEvent("Sync Completed", { final_progress: progress });
}

export function trackOnboardingCompleted() {
  trackEvent("Onboarding Completed");
}

// ── Auth Events ──

export function trackSignIn(method: string) {
  trackEvent("Sign In", { method });
}

export function trackSignOut() {
  trackEvent("Sign Out");
}

// ── Closet Events ──

export function trackItemViewed(item: { id: string; name: string; brand: string; category?: string; price?: string }) {
  trackEvent("Item Viewed", item);
}

export function trackSearch(query: string) {
  trackEvent("Search Performed", { query });
}

export function trackSortChanged(sortOption: string) {
  trackEvent("Sort Changed", { sort_option: sortOption });
}

export function trackViewModeChanged(mode: string) {
  trackEvent("View Mode Changed", { view_mode: mode });
}

export function trackMemberSelected(memberId: string) {
  trackEvent("Member Selected", { member_id: memberId });
}

export function trackCategoryFiltered(categoryName: string | null) {
  trackEvent("Category Filtered", { category_name: categoryName });
}

// ── Share Events ──

export function trackShareModalOpened() {
  trackEvent("Share Modal Opened");
}

export function trackInventoryCopied() {
  trackEvent("Inventory Copied");
}

export function trackInventoryDownloaded() {
  trackEvent("Inventory Downloaded");
}

export function trackShareLinkCopied() {
  trackEvent("Share Link Copied");
}

export function trackLogoutConfirmed() {
  trackEvent("Logout Confirmed");
}
